/**
 * FinancialDecisionSheet - Post-completion verdict display for Financial blueprint.
 *
 * Based on mockup_financial.html. Shows structured financial analysis results:
 * 1. Ticker header (symbol + company + price + BULL/BEAR verdict badge)
 * 2. TL;DR callout (gold border, serif)
 * 3. "What We Caught" banner (rejected argument)
 * 4. "What To Do" two-column card (hold / entry actions)
 * 5. Kill Sheet section (rejected arguments)
 * 6. Bull vs Bear side-by-side (intensity bars + thesis)
 * 7. Collapsible: Debate Record
 * 8. Collapsible: Sources & Data
 */

import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ShieldX,
  XCircle,
  Swords,
  Database,
  CheckCircle2,
  RefreshCw,
  Newspaper,
  ExternalLink,
} from 'lucide-react';
import type { FinancialKillSheet, AnchoredSnapshot } from '@/components/verdict/FinancialAnalysisPanel';
import { deriveFinancialTldr, deriveFinancialActions } from '@/utils/extractDecisionSheetData';
import { MarkdownContent } from './MarkdownContent';

// ============================================================================
// TYPES
// ============================================================================

interface FinancialDecisionSheetProps {
  killSheet: FinancialKillSheet;
  anchoredSnapshot?: AnchoredSnapshot;
  convergenceTrajectory?: number[];
  initialDivergence?: number;
  echoChamberedDetected?: boolean;
  query: string;
  onNewResearch?: () => void;
  className?: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function FinancialDecisionSheet({
  killSheet,
  anchoredSnapshot,
  convergenceTrajectory,
  initialDivergence,
  echoChamberedDetected,
  query,
  onNewResearch,
  className,
}: FinancialDecisionSheetProps) {
  const isBear = killSheet.winning_side === 'bear';
  const verdictColor = isBear ? 'text-red-500' : 'text-teal-500';
  const verdictBgClass = isBear
    ? 'bg-red-500/15 border-red-500/30'
    : 'bg-teal-500/15 border-teal-500/30';

  // TL;DR
  const tldr = useMemo(() => deriveFinancialTldr(killSheet), [killSheet]);

  // Actions
  const actions = useMemo(() => deriveFinancialActions(killSheet), [killSheet]);

  // Most impactful rejected argument for "What We Caught"
  const topRejection = killSheet.rejected_arguments?.[0];

  // Price data from snapshot
  const priceDisplay = anchoredSnapshot?.priceFormatted || (anchoredSnapshot?.price ? `$${anchoredSnapshot.price.toFixed(2)}` : null);
  const changeDisplay = anchoredSnapshot?.changePercentFormatted || (anchoredSnapshot?.changePercent ? `${anchoredSnapshot.changePercent > 0 ? '+' : ''}${anchoredSnapshot.changePercent.toFixed(1)}%` : null);
  const changeIsNegative = (anchoredSnapshot?.changePercent ?? 0) < 0;

  return (
    <div className={cn('w-full', className)}>
      {/* ── TICKER HEADER ── */}
      <div className="pb-5 border-b border-border mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-baseline gap-2.5">
            <span className="text-[28px] font-bold text-text-primary tracking-tight">{killSheet.ticker}</span>
            {anchoredSnapshot?.companyName && (
              <span className="text-sm text-text-secondary">{anchoredSnapshot.companyName}</span>
            )}
          </div>
          {priceDisplay && (
            <div className="flex items-baseline gap-2 ml-auto">
              <span className="text-xl font-mono font-medium text-text-primary">{priceDisplay}</span>
              {changeDisplay && (
                <span className={cn('text-sm font-mono', changeIsNegative ? 'text-red-500' : 'text-teal-500')}>
                  {changeDisplay}
                </span>
              )}
            </div>
          )}
          <div className={cn('inline-flex items-center gap-2 px-4 py-2 rounded-lg border', verdictBgClass)}>
            {isBear ? (
              <TrendingDown className={cn('w-[18px] h-[18px]', verdictColor)} />
            ) : (
              <TrendingUp className={cn('w-[18px] h-[18px]', verdictColor)} />
            )}
            <span className={cn('text-base font-bold tracking-wide', verdictColor)}>
              {killSheet.winning_side.toUpperCase()}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3.5 flex-wrap">
          <span className="inline-flex items-center bg-verdict-primary/[0.12] border border-verdict-primary/25 text-verdict-primary text-[13px] font-semibold px-3 py-1 rounded-full">
            {Math.round(killSheet.confidence)}% Confidence
          </span>
          <span className="text-[13px] text-text-muted">{killSheet.debate_rounds} debate rounds</span>
          {anchoredSnapshot?.asOf && (
            <span className="text-[13px] text-text-muted">
              Data as of {new Date(anchoredSnapshot.asOf).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          )}
        </div>
      </div>

      {/* ── QUESTION CONTEXT ── */}
      <div className="text-[13px] text-text-muted mb-5 px-4 py-3 bg-surface-light rounded-md border border-border">
        <span className="text-text-secondary italic">"{query}"</span>
      </div>

      {/* ── TL;DR CALLOUT ── */}
      <div className="border-l-4 border-verdict-primary bg-surface/70 backdrop-blur-xl rounded-r-xl px-6 py-5 mb-5 shadow-[0_4px_20px_rgba(212,165,116,0.15)]">
        <div className="text-[11px] font-semibold tracking-[0.15em] uppercase text-text-muted mb-2.5">
          Bottom Line
        </div>
        <div className="text-xl leading-relaxed text-text-primary">
          {tldr}
        </div>
        <div className="mt-3 text-xs text-text-muted">
          Based on {killSheet.total_agents} agents &bull; {killSheet.debate_rounds} debate rounds
        </div>
      </div>

      {/* ── WHAT WE CAUGHT BANNER ── */}
      {topRejection && (
        <div className="border-l-4 border-red-500 bg-red-500/[0.06] rounded-r-xl px-5 py-4 mb-5">
          <div className="flex items-start gap-3.5">
            <ShieldX className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-red-500 mb-1.5">
                Argument Rejected
              </div>
              <div className="text-[15px] text-text-secondary leading-relaxed mb-2">
                {topRejection.argument_summary}: {topRejection.rejection_reason}
              </div>
              {killSheet.rejected_arguments.length > 1 && (
                <div className="text-sm text-red-400/80">
                  +{killSheet.rejected_arguments.length - 1} more rejected argument{killSheet.rejected_arguments.length > 2 ? 's' : ''} in kill sheet below
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── WHAT TO DO ── */}
      {(actions.holdActions.length > 0 || actions.entryActions.length > 0) && (
        <div className="bg-surface border border-border rounded-xl p-6 mb-5">
          <div className="flex gap-0">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-text-secondary pb-2 border-b border-border mb-3">
                If You Hold {killSheet.ticker}
              </div>
              {actions.holdActions.map((action, i) => (
                <div key={i} className="flex items-start gap-2 text-[15px] text-text-primary leading-relaxed mb-2">
                  <span className="text-text-muted flex-shrink-0 mt-0.5">&bull;</span>
                  <span>{action}</span>
                </div>
              ))}
              {/* Supplement with watch items */}
              {killSheet.watch_items?.slice(0, 2).map((item, i) => (
                <div key={`w${i}`} className="flex items-start gap-2 text-[15px] text-text-primary leading-relaxed mb-2">
                  <span className="text-text-muted flex-shrink-0 mt-0.5">&bull;</span>
                  <span>Watch: {item.trigger} ({item.timeframe})</span>
                </div>
              ))}
            </div>
            <div className="flex-1 min-w-0 border-l border-border pl-6">
              <div className="text-[11px] font-bold tracking-[0.12em] uppercase text-text-secondary pb-2 border-b border-border mb-3">
                If Considering Entry
              </div>
              {actions.entryActions.map((action, i) => (
                <div key={i} className="flex items-start gap-2 text-[15px] text-text-primary leading-relaxed mb-2">
                  <span className="text-text-muted flex-shrink-0 mt-0.5">&bull;</span>
                  <span>{action}</span>
                </div>
              ))}
              {/* Supplement with catalysts */}
              {killSheet.catalysts?.slice(0, 2).map((cat, i) => (
                <div key={`c${i}`} className="flex items-start gap-2 text-[15px] text-text-primary leading-relaxed mb-2">
                  <span className="text-text-muted flex-shrink-0 mt-0.5">&bull;</span>
                  <span>{cat}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-text-muted italic mt-4 pt-3 border-t border-border">
            This is analysis, not financial advice.
          </div>
        </div>
      )}

      {/* ── KILL SHEET ── */}
      {killSheet.rejected_arguments.length > 0 && (
        <KillSheetSection rejectedArguments={killSheet.rejected_arguments} />
      )}

      {/* ── BULL VS BEAR ── */}
      <div className="flex gap-4 mt-5 mb-5">
        {/* Bull Card */}
        <div className="flex-1 min-w-0 rounded-xl p-5 border-l-4 border-teal-500 bg-teal-500/[0.04] border-t border-r border-b border-t-teal-500/15 border-r-teal-500/15 border-b-teal-500/15">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-[18px] h-[18px] text-teal-500" />
            <span className="text-sm font-bold uppercase tracking-wide text-teal-500">Bull Case</span>
          </div>
          {killSheet.bull_intensity !== undefined && (
            <div className="flex items-center gap-2.5 mb-3.5">
              <span className="text-xs text-text-muted whitespace-nowrap">Intensity: {killSheet.bull_intensity}/100</span>
              <div className="flex-1 h-1 bg-surface-raised rounded-full overflow-hidden">
                <div className="h-full bg-teal-500 rounded-full transition-all duration-500" style={{ width: `${killSheet.bull_intensity}%` }} />
              </div>
            </div>
          )}
          <MarkdownContent content={killSheet.bull_thesis || ''} className="text-[15px] text-text-secondary leading-relaxed" />
        </div>

        {/* Bear Card */}
        <div className="flex-1 min-w-0 rounded-xl p-5 border-l-4 border-red-500 bg-red-500/[0.04] border-t border-r border-b border-t-red-500/15 border-r-red-500/15 border-b-red-500/15">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="w-[18px] h-[18px] text-red-500" />
            <span className="text-sm font-bold uppercase tracking-wide text-red-500">Bear Case</span>
          </div>
          {killSheet.bear_risk !== undefined && (
            <div className="flex items-center gap-2.5 mb-3.5">
              <span className="text-xs text-text-muted whitespace-nowrap">Intensity: {killSheet.bear_risk}/100</span>
              <div className="flex-1 h-1 bg-surface-raised rounded-full overflow-hidden">
                <div className="h-full bg-red-500 rounded-full transition-all duration-500" style={{ width: `${killSheet.bear_risk}%` }} />
              </div>
            </div>
          )}
          <MarkdownContent content={killSheet.bear_thesis || ''} className="text-[15px] text-text-secondary leading-relaxed" />
        </div>
      </div>

      {/* ── DEBATE RECORD (COLLAPSIBLE) ── */}
      {convergenceTrajectory && convergenceTrajectory.length > 0 && (
        <CollapsibleSection
          icon={<Swords className="w-[18px] h-[18px] text-text-muted" />}
          title="Debate Record"
          meta={`${killSheet.debate_rounds} rounds`}
          preview={`Bull and bear teams debated for ${killSheet.debate_rounds} rounds. ${echoChamberedDetected ? 'Echo chamber detected.' : ''}`}
        >
          <div>
            {convergenceTrajectory.map((score, idx) => (
              <div key={idx} className="py-2.5 border-b border-border last:border-b-0">
                <div className="text-[11px] font-bold uppercase tracking-wider text-text-muted mb-1">
                  Round {idx + 1}
                </div>
                <div className="text-sm text-text-secondary leading-relaxed">
                  Agreement score: {Math.round(score * 100)}%
                  {idx === 0 && initialDivergence !== undefined && (
                    <span className="text-text-muted"> (initial divergence: {Math.round(initialDivergence * 100)}%)</span>
                  )}
                </div>
              </div>
            ))}
            {echoChamberedDetected && (
              <div className="mt-3 text-xs text-amber-500 font-medium">
                Echo chamber detected — convergence may reflect groupthink rather than genuine agreement.
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* ── SOURCES & DATA (COLLAPSIBLE) ── */}
      <SourcesAndDataSection
        dataSources={killSheet.data_sources}
        recentNews={anchoredSnapshot?.recentNews}
      />

      {/* ── FOOTER ── */}
      {onNewResearch && (
        <div className="mt-8 flex items-center gap-3">
          <button
            onClick={onNewResearch}
            className="flex items-center gap-2 px-4 py-2 bg-surface hover:bg-surface-light border border-border rounded-lg text-text-secondary text-sm font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            New Analysis
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// KILL SHEET SECTION
// ============================================================================

function KillSheetSection({ rejectedArguments }: { rejectedArguments: FinancialKillSheet['rejected_arguments'] }) {
  return (
    <div className="mt-5 mb-5">
      <div className="flex items-center gap-2.5 mb-3">
        <ShieldX className="w-5 h-5 text-red-500" />
        <span className="text-base font-bold text-text-primary">Kill Sheet</span>
        <span className="text-[13px] text-text-muted">{rejectedArguments.length} Rejected Argument{rejectedArguments.length > 1 ? 's' : ''}</span>
      </div>
      <div className="space-y-2.5">
        {rejectedArguments.map((arg, idx) => (
          <KillSheetCard key={idx} argument={arg} />
        ))}
      </div>
    </div>
  );
}

function KillSheetCard({ argument }: { argument: FinancialKillSheet['rejected_arguments'][number] }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const badge = (() => {
    const reason = argument.rejection_reason.toLowerCase();
    if (reason.includes('contradicted') || reason.includes('contradict')) return { label: 'Contradicted', className: 'bg-red-500/15 text-red-500' };
    if (reason.includes('speculative') || reason.includes('speculation')) return { label: 'Speculative', className: 'bg-amber-500/15 text-amber-500' };
    if (reason.includes('stale') || reason.includes('outdated') || reason.includes('old')) return { label: 'Stale Data', className: 'bg-orange-400/15 text-orange-400' };
    return { label: 'Rejected', className: 'bg-red-500/15 text-red-500' };
  })();

  return (
    <div className="bg-surface border border-border rounded-[10px] p-4">
      <div className="flex items-center gap-2.5 mb-1">
        <XCircle className="w-[18px] h-[18px] text-red-500 flex-shrink-0" />
        <span className="text-[15px] font-bold text-text-primary flex-1 min-w-0">{argument.argument_summary}</span>
        <span className={cn('text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded flex-shrink-0', badge.className)}>
          {badge.label}
        </span>
      </div>
      <div className="text-xs text-text-muted mb-2 pl-[30px]">
        Argued by {argument.side} Team ({argument.agent_role})
      </div>
      <div className="pl-[30px] mb-2">
        <MarkdownContent content={argument.rejection_detail || argument.rejection_reason || ''} className="text-[15px] text-text-secondary leading-relaxed" />
      </div>
      {argument.counter_evidence && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-[13px] text-red-400 font-medium pl-[30px] hover:underline cursor-pointer"
          >
            {isExpanded ? 'Hide counter-evidence \u2190' : 'View counter-evidence \u2192'}
          </button>
          <div className={cn(
            'overflow-hidden transition-all duration-300 pl-[30px]',
            isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
          )}>
            <div className="pt-2.5 mt-2 border-t border-border text-sm text-text-secondary leading-relaxed">
              <strong>Counter-evidence:</strong>
              <MarkdownContent content={argument.counter_evidence || ''} className="text-sm mt-1" inline />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// COLLAPSIBLE SECTION
// ============================================================================

function CollapsibleSection({
  icon,
  title,
  meta,
  preview,
  defaultOpen = false,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  meta?: string;
  preview?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2.5 px-5 py-4 hover:bg-surface-light transition-colors cursor-pointer select-none"
      >
        {icon}
        <span className="text-[15px] font-bold text-text-primary">{title}</span>
        {meta && <span className="text-[13px] text-text-muted ml-1">{meta}</span>}
        <ChevronDown className={cn(
          'w-4 h-4 text-text-muted ml-auto transition-transform duration-300',
          isOpen && 'rotate-180',
        )} />
      </button>
      {!isOpen && preview && (
        <div className="text-[13px] text-text-secondary px-5 pb-3.5 -mt-1.5 pl-[46px]">
          {preview}
        </div>
      )}
      <div className={cn(
        'overflow-hidden transition-all duration-300',
        isOpen ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0',
      )}>
        <div className="px-5 pb-5 pl-[46px]">
          <div className="border-t border-border pt-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SOURCES & DATA (with news + expandable anchors)
// ============================================================================

type NewsItem = NonNullable<AnchoredSnapshot['recentNews']>[number];

function SourcesAndDataSection({
  dataSources,
  recentNews,
}: {
  dataSources: Record<string, string>;
  recentNews?: NewsItem[];
}) {
  const [showAllNews, setShowAllNews] = useState(false);

  const dataSourceEntries = Object.entries(dataSources);
  const newsItems = recentNews ?? [];
  const totalSources = dataSourceEntries.length + newsItems.length;

  if (totalSources === 0) return null;

  const INITIAL_NEWS_COUNT = 3;
  const visibleNews = showAllNews ? newsItems : newsItems.slice(0, INITIAL_NEWS_COUNT);
  const hasMoreNews = newsItems.length > INITIAL_NEWS_COUNT;

  function getSourceBadge(name: string) {
    const lower = name.toLowerCase();
    if (lower.includes('sec') || lower.includes('10-k') || lower.includes('10-q'))
      return { label: 'SEC Filing', className: 'bg-blue-500/15 text-blue-400' };
    if (lower.includes('fmp') || lower.includes('financial'))
      return { label: 'Market Data', className: 'bg-teal-500/15 text-teal-400' };
    return { label: 'Data', className: 'bg-surface-light text-text-secondary' };
  }

  function getNewsBadge(item: NewsItem) {
    const site = (item.site || item.publisher || item.source || '').toLowerCase();
    if (site.includes('yahoo'))
      return { label: 'Yahoo Finance', className: 'bg-purple-500/15 text-purple-400' };
    if (site.includes('reuters') || site.includes('ap '))
      return { label: 'Wire', className: 'bg-blue-500/15 text-blue-400' };
    if (site.includes('bloomberg') || site.includes('wsj') || site.includes('cnbc'))
      return { label: 'Major Outlet', className: 'bg-amber-500/15 text-amber-400' };
    return { label: 'News', className: 'bg-surface-light text-text-secondary' };
  }

  return (
    <div className="mt-3">
      <CollapsibleSection
        icon={<Database className="w-[18px] h-[18px] text-text-muted" />}
        title="Sources & Data"
        meta={`${totalSources} sources`}
        preview={`${dataSourceEntries.length} data sources${newsItems.length > 0 ? ` and ${newsItems.length} news articles` : ''} analyzed.`}
      >
        <div>
          {/* Data anchors */}
          {dataSourceEntries.map(([name, description], idx) => {
            const badge = getSourceBadge(name);
            return (
              <div key={`data-${idx}`} className={cn('flex items-center gap-2.5 py-2.5', idx > 0 && 'border-t border-border/50')}>
                <CheckCircle2 className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                <span className="text-sm text-text-primary flex-1">{name}</span>
                <span className={cn('text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0', badge.className)}>
                  {badge.label}
                </span>
                <span className="text-[11px] text-text-muted flex-shrink-0">{description}</span>
              </div>
            );
          })}

          {/* News sources */}
          {newsItems.length > 0 && (
            <>
              {dataSourceEntries.length > 0 && (
                <div className="flex items-center gap-2 py-3 border-t border-border/50">
                  <Newspaper className="w-3.5 h-3.5 text-text-muted" />
                  <span className="text-xs font-medium uppercase tracking-wider text-text-muted">
                    News Sources ({newsItems.length})
                  </span>
                </div>
              )}
              {visibleNews.map((item, idx) => {
                const badge = getNewsBadge(item);
                const url = item.url || item.link;
                const siteName = item.site || item.publisher || item.source || 'Unknown';
                const date = item.publishedDate || item.published_date;
                return (
                  <div key={`news-${idx}`} className={cn('flex items-start gap-2.5 py-2.5', idx > 0 && 'border-t border-border/30')}>
                    <CheckCircle2 className="w-3.5 h-3.5 text-text-muted flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-text-primary hover:text-verdict-primary transition-colors line-clamp-1 flex items-center gap-1"
                        >
                          {item.title}
                          <ExternalLink className="w-3 h-3 flex-shrink-0 text-text-muted" />
                        </a>
                      ) : (
                        <span className="text-sm text-text-primary line-clamp-1">{item.title}</span>
                      )}
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-text-muted">{siteName}</span>
                        {date && (
                          <span className="text-[11px] text-text-muted">
                            {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={cn('text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0', badge.className)}>
                      {badge.label}
                    </span>
                  </div>
                );
              })}
              {hasMoreNews && (
                <button
                  onClick={() => setShowAllNews(!showAllNews)}
                  className="w-full text-center text-xs text-verdict-primary hover:text-verdict-primary-hover font-medium py-2 mt-1 border-t border-border/50 cursor-pointer transition-colors"
                >
                  {showAllNews ? 'Show less' : `Show all ${newsItems.length} news sources`}
                </button>
              )}
            </>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}
